/**
 * A lista de mensagens.
 *
 * O trabalho difícil aqui não é desenhar — é o SCROLL. Duas invariantes:
 *   1. quem está no fundo continua no fundo quando chega mensagem nova;
 *   2. quem carrega histórico para trás fica a olhar exactamente para a mesma
 *      linha — a altura nova é compensada no mesmo frame (`useLayoutEffect`),
 *      antes do browser pintar, senão vê-se o salto.
 * Quem está a ler a meio NÃO é arrastado para o fundo: é a diferença entre um
 * cliente que se usa e um que irrita.
 *
 * A procura acrescentou uma terceira: a lista pode estar DESLIGADA do presente
 * (saltou-se para uma mensagem de Março). Aí não se cola ao fundo — o fundo
 * desta janela não é o fim do canal — e mostra-se a saída de volta.
 */

import { type Attachment, Limits, type Message, Permission, type Snowflake } from "@greenvoice/shared";
import { type MouseEvent, type UIEvent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { formatBytes, formatDay, formatMessageStamp, formatTime, sameDay } from "../lib/format";
import { can, memberColor, selfPermissions, threadOfMessage } from "../store/selectors";
import { memberKey, useStore } from "../store/store";
import { Avatar } from "./Avatar";
import { EmojiGlyph } from "./EmojiGlyph";
import { EmojiPickerButton } from "./EmojiPicker";
import { IconArrowDown, IconCheckSquare, IconEdit, IconPin, IconReply, IconThread, IconTrash } from "./Icons";
import { RichText } from "./RichText";
import { ThreadCreate } from "./ThreadCreate";
import { UserCard } from "./UserCard";

/** Janela dentro da qual duas mensagens do mesmo autor se juntam num bloco. */
const GROUP_WINDOW_MS = 7 * 60 * 1000;
/** Distância ao fundo abaixo da qual se considera que o utilizador está "no fim". */
const STICKY_PX = 140;
/** Distância ao topo a partir da qual se pede o lote anterior. */
const PREFETCH_PX = 320;
/** Quanto tempo a mensagem a que se saltou fica marcada antes de voltar ao normal. */
const PISCA_MS = 2600;

const QUICK_REACTIONS = ["👍", "🎉", "✅", "👀", "❤️"] as const;

interface MessageListProps {
  channelId: Snowflake;
  onReply(message: Message): void;
}

export function MessageList({ channelId, onReply }: MessageListProps) {
  const state = useStore();
  const messages = state.messages[channelId] ?? [];
  const history = state.history[channelId];
  const channel = state.channels[channelId];

  const viewport = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const previous = useRef({ height: 0, count: 0, firstId: "", channelId: "" });

  useLayoutEffect(() => {
    const element = viewport.current;
    if (!element) return;

    const first = messages[0];
    const prev = previous.current;
    const switched = prev.channelId !== channelId;
    const prepended = !switched && prev.count > 0 && messages.length > prev.count && first?.id !== prev.firstId;

    // Numa janela desligada do presente o fundo da lista não é o fim do canal:
    // colar-se a ele saltava por cima do lote que se acabou de pedir para a
    // frente. Lê-se agora e não das dependências para o efeito não voltar a
    // correr — e a puxar o scroll — só porque o `atEnd` mudou.
    const separada = useStore.getState().history[channelId]?.atEnd === false;

    if (switched) {
      element.scrollTop = element.scrollHeight;
      atBottom.current = true;
    } else if (prepended) {
      // Compensa a altura que acabou de aparecer por cima.
      element.scrollTop += element.scrollHeight - prev.height;
    } else if (atBottom.current && !separada) {
      element.scrollTop = element.scrollHeight;
    }

    previous.current = {
      height: element.scrollHeight,
      count: messages.length,
      firstId: first?.id ?? "",
      channelId,
    };
  }, [messages, channelId]);

  /**
   * Salto para uma mensagem — é o que acontece ao carregar num resultado da
   * procura. DEPOIS do efeito de cima de propósito: os dois querem mexer no
   * scroll no mesmo frame (trocar de canal manda para o fundo) e ganha o último
   * a correr, que tem de ser este.
   *
   * Se a mensagem ainda não estiver desenhada não se faz nada e não se limpa o
   * alvo: a janela `around` está a chegar, e o render seguinte volta aqui.
   */
  const [aceso, setAceso] = useState<Snowflake | null>(null);
  const { jumpTarget, clearJumpTarget } = state;

  useLayoutEffect(() => {
    if (!jumpTarget) return;
    const linha = viewport.current?.querySelector(`[data-message-id="${jumpTarget}"]`);
    if (!linha) return;

    linha.scrollIntoView({ block: "center" });
    atBottom.current = false;
    previous.current.height = viewport.current?.scrollHeight ?? previous.current.height;
    setAceso(jumpTarget);
    clearJumpTarget();
  }, [jumpTarget, messages, clearJumpTarget]);

  useEffect(() => {
    if (!aceso) return;
    const timer = window.setTimeout(() => setAceso(null), PISCA_MS);
    return () => window.clearTimeout(timer);
  }, [aceso]);

  // Imagens mudam a altura DEPOIS do layout: sem isto, uma mensagem com foto
  // deixava metade do texto abaixo do fundo visível.
  useEffect(() => {
    const element = viewport.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (atBottom.current) element.scrollTop = element.scrollHeight;
    });
    // A lista, não `firstElementChild`: a barra de selecção passa a ser o
    // primeiro filho quando está ligada, e observá-la não mede nada.
    observer.observe(inner.current ?? element);
    return () => observer.disconnect();
  }, [channelId]);

  function onScroll(event: UIEvent<HTMLDivElement>) {
    const element = event.currentTarget;
    const aoFundo = element.scrollHeight - element.scrollTop - element.clientHeight;
    atBottom.current = aoFundo < STICKY_PX;
    if (!history?.loaded || history.loading) return;

    if (element.scrollTop < PREFETCH_PX && !history.atStart) {
      const oldest = messages[0];
      if (oldest) void state.loadMessages(channelId, { before: oldest.id });
      return;
    }

    // Só numa janela que não chega ao presente: no caso normal a lista já
    // termina na última mensagem do canal e não há nada para pedir à frente.
    if (aoFundo < PREFETCH_PX && !history.atEnd) {
      const newest = messages[messages.length - 1];
      if (newest) void state.loadMessages(channelId, { after: newest.id });
    }
  }

  const permissions = selfPermissions(state, channel?.serverId ?? null, channel);
  const canManage = can(permissions, Permission.MANAGE_MESSAGES);
  const canReact = can(permissions, Permission.ADD_REACTIONS);
  // Dentro de um tópico não se abrem tópicos, e num DM também não: sem servidor
  // não há permissões a herdar que segurem quem entra. A mesma regra do servidor,
  // repetida aqui só para não mostrar um botão que só devolveria 400.
  const canThread =
    (channel?.type === "TEXT" || channel?.type === "ANNOUNCEMENT") && can(permissions, Permission.CREATE_THREADS);

  /**
   * `null` = fora do modo de selecção. Um conjunto (mesmo vazio) = a escolher.
   * Apagar uma a uma dá pelo caminho a quem tem de limpar trinta linhas de spam;
   * o `POST /bulk-delete` já existia há muito e nunca teve por onde ser chamado.
   */
  const [seleccao, setSeleccao] = useState<ReadonlySet<Snowflake> | null>(null);
  const [apagando, setApagando] = useState(false);
  const [erroLote, setErroLote] = useState<string | null>(null);

  // Trocar de canal sai do modo: ids de outro canal são recusados pelo servidor.
  useEffect(() => {
    setSeleccao(null);
    setApagando(false);
    setErroLote(null);
  }, [channelId]);

  function alternar(id: Snowflake) {
    setErroLote(null);
    setSeleccao((prev) => {
      const proximo = new Set(prev ?? []);
      if (proximo.has(id)) proximo.delete(id);
      else if (proximo.size >= Limits.bulkDeleteMax) return prev;
      else proximo.add(id);
      return proximo;
    });
  }

  async function apagarLote() {
    if (!seleccao || seleccao.size === 0 || apagando) return;
    setApagando(true);
    setErroLote(null);
    try {
      // Sem remoção optimista: o evento `MessageBulkDelete` volta pelo gateway
      // e é o `store` que tira as linhas — assim toda a gente vê o mesmo.
      await api.messages.bulkDelete(channelId, [...seleccao]);
      setSeleccao(null);
    } catch (caught) {
      setErroLote((caught as Error).message);
    } finally {
      setApagando(false);
    }
  }

  const escolhidas = seleccao?.size ?? 0;
  const cheio = escolhidas >= Limits.bulkDeleteMax;

  return (
    <div className="messages" ref={viewport} onScroll={onScroll}>
      {seleccao ? (
        <div className="bulk-bar">
          <span className="bulk-bar__count">
            {escolhidas === 0
              ? "Escolhe as mensagens a apagar"
              : `${escolhidas} de ${Limits.bulkDeleteMax} selecionada${escolhidas === 1 ? "" : "s"}`}
          </span>
          {erroLote ? <span className="bulk-bar__error">{erroLote}</span> : null}
          <button
            type="button"
            className="button button--small button--danger"
            disabled={escolhidas === 0 || apagando}
            onClick={() => {
              if (window.confirm(`Apagar ${escolhidas} mensagens? Não há como voltar atrás.`)) void apagarLote();
            }}
          >
            {apagando ? "A apagar…" : `Apagar ${escolhidas}`}
          </button>
          <button type="button" className="button button--small" onClick={() => setSeleccao(null)}>
            Cancelar
          </button>
        </div>
      ) : null}

      <div className="messages__inner" ref={inner}>
        {history?.atStart || (history?.loaded && messages.length === 0) ? (
          <ChannelIntro name={channel?.name ?? ""} topic={channel?.topic ?? null} />
        ) : null}

        {history?.loading && messages.length > 0 ? (
          <p className="messages__status">Carregando mensagens anteriores…</p>
        ) : null}

        {history?.error ? (
          <p className="messages__status messages__status--error">
            {history.error}{" "}
            <button type="button" className="link" onClick={() => void state.loadMessages(channelId)}>
              Tentar de novo
            </button>
          </p>
        ) : null}

        {messages.map((message, index) => {
          const before = messages[index - 1];
          const divider = !before || !sameDay(before.createdAt, message.createdAt);
          const grouped =
            !divider &&
            before !== undefined &&
            before.authorId === message.authorId &&
            message.type === "DEFAULT" &&
            before.type !== "MEMBER_JOIN" &&
            Date.parse(message.createdAt) - Date.parse(before.createdAt) < GROUP_WINDOW_MS;

          const escolhida = seleccao?.has(message.id) ?? false;
          const linha = (
            <MessageRow
              message={message}
              grouped={grouped}
              canManage={canManage}
              canReact={canReact}
              canThread={canThread}
              onReply={onReply}
              onSelect={seleccao ? undefined : () => alternar(message.id)}
            />
          );

          return (
            <div
              key={message.id}
              data-message-id={message.id}
              className={aceso === message.id ? "messages__row is-jumped" : "messages__row"}
            >
              {divider ? (
                <div className="day-divider">
                  <span>{formatDay(message.createdAt)}</span>
                </div>
              ) : null}
              {seleccao ? (
                // A caixa fica FORA da `MessageRow` de propósito: assim também
                // apanha as mensagens de sistema, que saem por outro ramo — e
                // são precisamente as que enchem um canal de "entrou".
                <div className={`messages__pick${escolhida ? " is-picked" : ""}`}>
                  <input
                    type="checkbox"
                    className="messages__pick-box"
                    checked={escolhida}
                    disabled={cheio && !escolhida}
                    aria-label={`Selecionar mensagem de ${message.author.username}`}
                    onChange={() => alternar(message.id)}
                  />
                  <div className="messages__pick-body">{linha}</div>
                </div>
              ) : (
                linha
              )}
            </div>
          );
        })}
      </div>

      {/* Só existe enquanto a lista estiver desligada do presente. É a única
          saída: sem ela, quem salta para uma mensagem de Março fica preso a
          rolar para a frente até ao fim do histórico. */}
      {history && !history.atEnd ? (
        <button
          type="button"
          className="jump-present"
          onClick={() => {
            atBottom.current = true;
            void state.loadMessages(channelId);
          }}
        >
          <IconArrowDown size={15} />
          <span>Ir para o presente</span>
        </button>
      ) : null}
    </div>
  );
}

function ChannelIntro({ name, topic }: { name: string; topic: string | null }) {
  return (
    <div className="channel-intro">
      <h2>Bem-vindo a #{name}</h2>
      <p>{topic ?? "É aqui que começa esta conversa."}</p>
    </div>
  );
}

interface MessageRowProps {
  message: Message;
  grouped: boolean;
  canManage: boolean;
  canReact: boolean;
  canThread: boolean;
  onReply(message: Message): void;
  /** Entra no modo de selecção com esta mensagem já escolhida. Ausente quando
   *  já se está a seleccionar — aí a caixa de visto trata do assunto. */
  onSelect?: (() => void) | undefined;
}

function MessageRow({ message, grouped, canManage, canReact, canThread, onReply, onSelect }: MessageRowProps) {
  const state = useStore();
  const [threading, setThreading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content);
  /** Guarda-se o ELEMENTO, não um booleano: o cartão posiciona-se a partir dele
   *  (o contentor das mensagens tem overflow, logo o cartão vai para um portal)
   *  e usa-o para distinguir "carregou fora" de "carregou outra vez no nome". */
  const [cardAnchor, setCardAnchor] = useState<HTMLElement | null>(null);

  const isSelf = state.self?.id === message.authorId;
  const serverId = state.channels[message.channelId]?.serverId ?? null;
  const member = serverId ? state.members[memberKey(serverId, message.authorId)] : undefined;
  const color = memberColor(state, member);
  const name = message.member?.nickname ?? member?.nickname ?? message.author.displayName ?? message.author.username;
  const mentionsMe = state.self !== null && (message.mentionIds.includes(state.self.id) || message.mentionsEveryone);
  /**
   * O tópico que já saiu desta mensagem, se houver.
   *
   * Uma mensagem só pode gerar um (o `sourceMessageId` é único do lado da base
   * de dados). Sem isto o botão continuava a oferecer "abrir tópico" a quem já
   * tinha um e devolvia um 409 — por isso quando existe, o botão leva lá.
   */
  const existingThread = threadOfMessage(state, message.id);

  if (message.type !== "DEFAULT" && message.type !== "REPLY") {
    return <SystemMessage message={message} name={name} />;
  }

  function openCard(event: MouseEvent<HTMLButtonElement>) {
    const target = event.currentTarget;
    setCardAnchor((current) => (current ? null : target));
  }

  async function commitEdit() {
    const next = draft.trim();
    setEditing(false);
    if (!next || next === message.content) return;
    await state.editMessage(message.channelId, message.id, next).catch(() => undefined);
  }

  return (
    <article className={`message${grouped ? " message--grouped" : ""}${mentionsMe ? " message--mention" : ""}`}>
      {message.replyToId ? <ReplyBanner channelId={message.channelId} messageId={message.replyToId} /> : null}

      <div className="message__body">
        <div className="message__gutter">
          {grouped ? (
            <time className="message__hover-time" dateTime={message.createdAt}>
              {formatTime(message.createdAt)}
            </time>
          ) : (
            // O avatar abre o mesmo cartão que o nome: é o alvo maior e é onde
            // toda a gente carrega primeiro, sobretudo no telemóvel.
            <button type="button" className="message__avatar" onClick={openCard} aria-label={`Perfil de ${name}`}>
              <Avatar user={message.author} overrideUrl={message.member?.avatarUrl ?? null} size={40} />
            </button>
          )}
        </div>

        <div className="message__content">
          {grouped ? null : (
            <header className="message__head">
              <button
                type="button"
                className="message__author"
                style={color ? { color } : undefined}
                onClick={openCard}
              >
                {name}
              </button>
              {message.author.bot ? <span className="tag">BOT</span> : null}
              <time dateTime={message.createdAt} title={formatMessageStamp(message.createdAt)}>
                {formatMessageStamp(message.createdAt)}
              </time>
              {message.pinned ? <IconPin size={12} className="message__pin" /> : null}
            </header>
          )}

          {editing ? (
            <div className="message__edit">
              <textarea
                value={draft}
                autoFocus
                rows={Math.min(8, draft.split("\n").length)}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setEditing(false);
                    setDraft(message.content);
                  }
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void commitEdit();
                  }
                }}
              />
              <small>Enter para salvar · Esc para cancelar</small>
            </div>
          ) : (
            <div className="message__text">
              <RichText content={message.content} />
              {message.editedAt ? <span className="message__edited">(editado)</span> : null}
            </div>
          )}

          {message.attachments.length ? <Attachments items={message.attachments} /> : null}
          {message.reactions.length ? (
            <Reactions
              message={message}
              disabled={!canReact}
              onToggle={(emoji) =>
                void state.toggleReaction(message.channelId, message.id, emoji).catch(() => undefined)
              }
            />
          ) : null}
        </div>

        <div className="message__tools">
          {canReact
            ? QUICK_REACTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  className="message__tool"
                  title={`Reagir com ${emoji}`}
                  onClick={() => void state.toggleReaction(message.channelId, message.id, emoji).catch(() => undefined)}
                >
                  {emoji}
                </button>
              ))
            : null}
          {canReact ? (
            <EmojiPickerButton
              serverId={serverId}
              className="message__tool"
              title="Outro emoji"
              // `picked.value` e não `picked.token`: uma reacção guarda o id
              // pelado. Escrever aqui o `<:nome:id>` criava uma segunda bolha
              // para o mesmo emoji e ainda partia-a ao renomear.
              onPick={(picked) =>
                void state.toggleReaction(message.channelId, message.id, picked.value).catch(() => undefined)
              }
            />
          ) : null}
          <button type="button" className="message__tool" title="Responder" onClick={() => onReply(message)}>
            <IconReply size={16} />
          </button>
          {canThread || existingThread ? (
            <button
              type="button"
              className={`message__tool${existingThread ? " is-on" : ""}`}
              title={existingThread ? `Ver o tópico «${existingThread.name}»` : "Abrir tópico"}
              onClick={() => {
                // Já há tópico: leva lá em vez de oferecer um segundo. Quem
                // criou o primeiro pode não ser quem carrega agora, e um botão
                // que só devolve "essa mensagem já tem um tópico" não serve.
                if (existingThread) state.selectChannel(existingThread.id);
                else setThreading(true);
              }}
            >
              <IconThread size={16} />
            </button>
          ) : null}
          {isSelf ? (
            <button
              type="button"
              className="message__tool"
              title="Editar"
              onClick={() => {
                setDraft(message.content);
                setEditing(true);
              }}
            >
              <IconEdit size={16} />
            </button>
          ) : null}
          {canManage && onSelect ? (
            <button type="button" className="message__tool" title="Selecionar várias" onClick={onSelect}>
              <IconCheckSquare size={16} />
            </button>
          ) : null}
          {isSelf || canManage ? (
            <button
              type="button"
              className="message__tool message__tool--danger"
              title="Excluir"
              onClick={() => {
                if (window.confirm("Excluir esta mensagem?")) {
                  void state.deleteMessage(message.channelId, message.id).catch(() => undefined);
                }
              }}
            >
              <IconTrash size={16} />
            </button>
          ) : null}
        </div>
      </div>

      {threading ? (
        <ThreadCreate channelId={message.channelId} messageId={message.id} onClose={() => setThreading(false)} />
      ) : null}

      {cardAnchor ? (
        <UserCard
          user={message.author}
          anchor={cardAnchor}
          color={color}
          displayName={name}
          onClose={() => setCardAnchor(null)}
        />
      ) : null}
    </article>
  );
}

/** A mensagem a que se está a responder, em cima e em pequeno. */
function ReplyBanner({ channelId, messageId }: { channelId: Snowflake; messageId: Snowflake }) {
  const target = useStore((s) => (s.messages[channelId] ?? []).find((m) => m.id === messageId));

  return (
    <div className="reply-banner">
      <span className="reply-banner__spine" />
      {target ? (
        <>
          <Avatar user={target.author} size={16} />
          <strong>{target.author.displayName ?? target.author.username}</strong>
          <span className="reply-banner__text">{preview(target)}</span>
        </>
      ) : (
        <span className="reply-banner__text">Mensagem original indisponível.</span>
      )}
    </div>
  );
}

function preview(message: Message): string {
  const text = message.content.replace(/\s+/g, " ").trim();
  if (text) return text.length > 120 ? `${text.slice(0, 120)}…` : text;
  if (message.attachments.length) return `${message.attachments.length} arquivo(s)`;
  return "…";
}

function SystemMessage({ message, name }: { message: Message; name: string }) {
  const text =
    message.type === "MEMBER_JOIN"
      ? `${name} entrou no servidor.`
      : message.type === "MEMBER_LEAVE"
        ? `${name} saiu do servidor.`
        : message.type === "CHANNEL_RENAMED"
          ? `${name} mudou o nome do canal para ${message.content}.`
          : message.type === "PINNED_MESSAGE"
            ? `${name} fixou uma mensagem.`
            : `${name} iniciou uma chamada.`;

  return (
    <div className="system-message">
      <span className="system-message__dot" />
      <span>{text}</span>
      <time dateTime={message.createdAt}>{formatTime(message.createdAt)}</time>
    </div>
  );
}

function Attachments({ items }: { items: Attachment[] }) {
  return (
    <div className="attachments">
      {items.map((item) =>
        item.contentType.startsWith("image/") ? (
          <a key={item.id} href={item.url} target="_blank" rel="noopener noreferrer" className="attachment-image">
            <img
              src={item.url}
              alt={item.filename}
              loading="lazy"
              decoding="async"
              {...(item.width ? { width: item.width } : {})}
              {...(item.height ? { height: item.height } : {})}
            />
          </a>
        ) : item.contentType.startsWith("video/") ? (
          <video key={item.id} className="attachment-video" src={item.url} controls preload="metadata" />
        ) : item.contentType.startsWith("audio/") ? (
          <audio key={item.id} className="attachment-audio" src={item.url} controls preload="metadata" />
        ) : (
          <a
            key={item.id}
            className="attachment-file"
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            download={item.filename}
          >
            <strong>{item.filename}</strong>
            <small>{formatBytes(item.size)}</small>
          </a>
        ),
      )}
    </div>
  );
}

function Reactions({
  message,
  disabled,
  onToggle,
}: {
  message: Message;
  disabled: boolean;
  onToggle(emoji: string): void;
}) {
  return (
    <div className="reactions">
      {message.reactions.map((reaction) => (
        <button
          key={reaction.emoji}
          type="button"
          className={`reaction${reaction.me ? " is-mine" : ""}`}
          disabled={disabled}
          onClick={() => onToggle(reaction.emoji)}
        >
          <EmojiGlyph value={reaction.emoji} />
          <em>{reaction.count}</em>
        </button>
      ))}
    </div>
  );
}
